import styled from 'styled-components';
import React, { Component } from 'react';
import axios, { CancelTokenSource } from 'axios';
import { RouterProps } from 'react-router';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { withRouterWorkaround } from 'utils/workaroundUtils';
import ToolbarButton from 'components/layout/ToolbarButton';
import { Icon } from 'semantic-ui-react';
import { BillConsumerRenderProps, withBillContext } from 'components/bill/BillContext';
import { BillStatus } from 'ts-types/api.enums';
import { TreatmentDto } from 'ts-types/api.types';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { PatientConsumerRenderProps, withPatientContext } from 'components/patient/PatientContext';

const ActionsToolbarWrapper = styled.div`
  display: flex;
  padding: 0 0.425rem;
  margin-bottom: 0.7rem;
`;

const StyledToolbarBtn = styled(ToolbarButton)`
  &.clear-right-side {
    padding-right: 0 !important;
    margin-right: 0 !important;
  }
`;

const ButtonDivider = styled.div`
  display: inline-block;
  text-align: center;
  margin: 0 0.775rem;
  height: 1.4rem;
  border-left: 1px solid rgba(0, 0, 0, 0.175);
  cursor: default;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;

interface Props extends MessageSourceProps, RouterProps, BillConsumerRenderProps, AuthConsumerRenderProps, PatientConsumerRenderProps {
  selectedTreatment?: TreatmentDto
}

interface State {
  cancelTokenSource: CancelTokenSource
}

const ToolbarDivider = () => {
  return (
      <ButtonDivider>&nbsp;</ButtonDivider>
  )
}

class BillsToolbar extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      cancelTokenSource
    }
  }

  goToBillStorno = () => {
    const {selectedBill} = this.props;
    this.props.history.push("/storno/bill", {selectedBill: selectedBill})
  };

  goToPaymentStorno = () => {
    const {selectedBill} = this.props;
    this.props.history.push("/storno/payment", {selectedBill: selectedBill})
  };

  goToManualPayment = () => {
    const {selectedBill} = this.props;
    this.props.history.push("/manual-payment", {selectedBill: selectedBill})
  };

  goToManualBillReminder = () => {
    const {selectedBill} = this.props;
    this.props.history.push("/manual-reminder", {selectedBill: selectedBill})
  };

  goToDocumentCopies = () => {
    const {selectedBill} = this.props;
    this.props.history.push("/document-copies", {selectedBill: selectedBill})
  };

  render(): React.ReactNode {
    const {selectedBill, currentUser, currentPatient, selectedTreatment, getMessage} = this.props;
    const toolbarButtonStyle = {padding: '0', paddingRight: '1rem'};

    const isSelectedTreatmentDeleted = selectedTreatment && selectedTreatment.deleted === 1;

    let disableBtn = (!currentUser || !currentPatient || !selectedTreatment
        || (currentUser.userConfig.mandateId !== selectedTreatment.mandateId)) || isSelectedTreatmentDeleted;

    const disableStornoBill = disableBtn ||
        (selectedBill && (selectedBill.status === BillStatus.PAID_OPEN || selectedBill.status === BillStatus.PAID_CLOSED));
    const disableManualPayment = disableBtn ||
        (selectedBill && (selectedBill.status !== BillStatus.PAID_OPEN && selectedBill.status === BillStatus.PAID_CLOSED));
    const disableStornoPayment = disableBtn ||
        (selectedBill && (selectedBill.status !== BillStatus.PAID_OPEN && selectedBill.status !== BillStatus.PAID_CLOSED));
    const disableManualReminder = disableBtn || (selectedBill && selectedBill.status === BillStatus.PAID_CLOSED);
    const disableCopies = disableBtn || (selectedBill && selectedBill.status === BillStatus.STORNO);

    return (
        <ActionsToolbarWrapper>
          <StyledToolbarBtn iconName="file alternate outline"
                            iconSize="large"
                            className="clear-right-side"
                            onClick={this.goToBillStorno}
                            disabled={disableStornoBill}
                            dataTooltip={getMessage("header.navigation.cancelBill")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="file alternate outline"/>
                                  <Icon corner="top right" name="delete" color="grey"/>
                                </Icon.Group>
                            )}
          />
          <ToolbarDivider/>
          <StyledToolbarBtn iconName='money bill alternate outline'
                            iconSize='large'
                            className="clear-right-side"
                            onClick={this.goToManualPayment}
                            disabled={disableManualPayment}
                            dataTooltip={getMessage("header.navigation.manualPayment")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="money bill alternate outline"/>
                                  <Icon corner="top right" name="magnify" color="grey"/>
                                </Icon.Group>
                            )}
          />
          <ToolbarDivider/>
          <StyledToolbarBtn iconName='money bill alternate outline'
                            iconSize='large'
                            className="clear-right-side"
                            onClick={this.goToPaymentStorno}
                            disabled={disableStornoPayment}
                            dataTooltip={getMessage("header.navigation.cancelPayment")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="money bill alternate outline"/>
                                  <Icon corner="top right" name="delete" color="grey"/>
                                </Icon.Group>
                            )}
          />
          <ToolbarDivider/>
          <StyledToolbarBtn iconName='money bill alternate outline'
                            iconSize='large'
                            className="clear-right-side"
                            onClick={this.goToManualBillReminder}
                            disabled={disableManualReminder}
                            dataTooltip={getMessage("manualReminder")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="bell outline"/>
                                </Icon.Group>
                            )}
          />
          <ToolbarDivider/>
          <StyledToolbarBtn iconName='copy outline'
                            iconSize='large'
                            className="clear-right-side"
                            onClick={this.goToDocumentCopies}
                            disabled={disableCopies}
                            dataTooltip={getMessage("documentCopies")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="copy outline"/>
                                </Icon.Group>
                            )}
          />
        </ActionsToolbarWrapper>
    )
  }
}

export default withRouterWorkaround(withMessages(withBillContext(withPatientContext(withAuthContext(BillsToolbar)))));
